<template>
    <v-form ref="setupForm">
        <v-card>
            <v-card-title>Experiment Setup</v-card-title>
            <v-card-text>
                <v-container class="text-center" grid-list-md>
                    <v-row>
                        <v-col md="2">
                            <v-select
                                v-model="numberOfExperiments"
                                :items="[1, 2, 3, 4, 5]"
                                label="Number of Experiments (*)"
                            />
                        </v-col>
                        <v-col md="3">
                            <v-checkbox
                                v-model="useConfluenceExperimentSetup"
                                label="Use Confluence"
                                :onchange="useConfluenceExperimentSetupCheckboxChanged"
                            />
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12" md="4">
                            <v-text-field
                                label="Name (*)"
                                v-model="name"
                                :rules="requiredTextValueRules('Experiment name')"
                                required
                            ></v-text-field>
                        </v-col>
                        <v-col md="4">
                            <v-autocomplete
                                :items="platforms"
                                label="Platform (*)"
                                clearable
                                v-model="selectedPlatform"
                                item-title="name"
                                return-object
                                :rules="requiredSimpleValueRules('Platform')"
                            />
                        </v-col>
                        <v-col md="4">
                            <v-select
                                v-model="objective"
                                :items="filteredObjectives"
                                label="Objective"
                                clearable
                            />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12" md="4">
                            <v-autocomplete
                                :items="filteredExperimentGroups"
                                label="Experiment Group (*)"
                                clearable
                                v-model="selectedExperimentGroupId"
                                item-title="name"
                                item-value="id"
                                :rules="requiredSimpleValueRules('Experiment group')"
                            />
                        </v-col>
                        <v-col md="4">
                            <v-date-input
                                v-model="startDate"
                                label="Day 0 (*)"
                                :rules="requiredTextValueRules('Day 0')"
                                required
                            ></v-date-input>
                        </v-col>
                        <v-col md="4">
                            <v-checkbox v-model="draft" label="Draft" />
                        </v-col>
                    </v-row>

                    <v-row>
                        <v-col cols="12">
                            <experiment-metadata-template-fields class="ml-n4"
                                 metadata-context="experiment"
                                 v-model="experimentMetadata"
                            />
                        </v-col>
                    </v-row>

                    <v-row v-if="isTreatmentsAndExperimentDesignConfigVisible" no-gutters>
                        <v-col cols="12">
                            <design-config-form class="ml-n4"
                                ref="experimentDesignConfigForm"
                                :model-value="experimentDesignConfig"
                                :experimental-unit-options="experimentalUnitOptions"
                                @update:modelValue="onExperimentDesignConfigFormChanged"
                            />
                        </v-col>
                    </v-row>

                    <v-row v-if="isTreatmentsAndExperimentDesignConfigVisible">
                        <treatments-file-upload-form class="mb-8"
                            v-model="treatmentsFile"
                            layout-orientation="vertical"
                        />
                    </v-row>

                    <experiment-relationship-setup-form
                        v-if="selectedExperimentGroupId"
                        :experimentRelationshipConfigForTypes="experimentRelationshipConfigForTypes"
                        :experiment-group="selectedExperimentGroup"
                        @onChanged="onRelationshipSetupFormChanged"
                    />
                </v-container>
            </v-card-text>
            <v-card-actions>
                <v-spacer />
                <v-btn color="primary" @click="cancel"> Cancel </v-btn>
                <v-btn color="primary" @click="save"> Submit </v-btn>
            </v-card-actions>
        </v-card>
    </v-form>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from "vue";
import { requiredSimpleValueRules, requiredTextValueRules } from "@/lib/validationUtils";
import {
    ExperimentGroupDTO,
    experimentGroupsAPI,
} from "@/services/experimentGroups/experimentGroupsAPI";
import { withLoadingScreen } from "@/lib/loading";
import router from "@/router";
import { EventBus } from "@/lib/event-bus";
import ExperimentRelationshipSetupForm from "@/components/ExperimentRelationshipConfigs/ExperimentRelationshipSetupForm.vue";
import { experimentsAPI, ExperimentSetupResult } from "@/services/experiments/experimentsAPI";
import ExperimentMetadataTemplateFields from "@/components/Experiments/ExperimentMetadataTemplateFields.vue";
import { buildExperimentObjectivesOptions } from "@/lib/experimentMetadata";
import { PlatformDTO } from "@/services/commonModels";
import {
    ExperimentRelationshipsForSetupDTO, RelationshipConfigForExperimentSetup,
    RelationshipConfigsForType,
} from "@/services/experimentRelationships/models";
import _ from "lodash";
import TreatmentsFileUploadForm from "@/components/Treatments/TreatmentsFileUploadForm.vue";
import {
    ExperimentalUnitOption,
    ExperimentDesignConfig,
    experimentDesignConfigsAPI,
} from "@/services/experimentDesignConfigs/experimentDesignConfigsAPI";
import DesignConfigForm from "@/components/ExperimentDesignConfigs/DesignConfigForm.vue";
import {
    experimentRelationshipConfigAPIForSetup
} from "@/services/experimentRelationships/experimentRelationshipConfigsAPI";

const setupForm = ref();
const useConfluenceExperimentSetup = ref(true);
const numberOfExperiments = ref(1);
const name = ref();

const experimentObjectives = ref<string[]>([]);
const objective = ref("");
const experimentGroups = ref<ExperimentGroupDTO[]>([]);
const selectedExperimentGroupId = ref<string>();
const platforms = ref<PlatformDTO[]>([]);
const selectedPlatform = ref<PlatformDTO | null>(null);
const startDate = ref(new Date());
const experimentMetadata = ref<Record<string, any>>({} as unknown as Record<string, any>);
const draft = ref(false);
const experimentRelationships = ref<ExperimentRelationshipsForSetupDTO[]>([]);

const experimentDesignConfigForm = ref();
const experimentDesignConfig = ref<ExperimentDesignConfig | null>(null);
const experimentalUnitOptions = ref<ExperimentalUnitOption[]>([]);

const experimentRelationshipConfigForTypes = ref<RelationshipConfigsForType<RelationshipConfigForExperimentSetup>[]>([]);

const treatmentsFile = ref<File | null>(null);

const emit = defineEmits<{
    (e: "exitConfluenceSetup"): void;
}>();

onMounted(async () => {
    await withLoadingScreen(async () => {
        const allExperimentGroups = await experimentGroupsAPI.fetchExperimentGroups();
        experimentGroups.value = allExperimentGroups.filter((experimentGroup) => {
            return experimentGroup.isAvailableInExperimentSetup;
        });
        platforms.value = await experimentsAPI.fetchPlatforms();
        experimentObjectives.value = buildExperimentObjectivesOptions(experimentGroups.value);
    });
});

/**
 * Not all experiment groups have a platform set, so we need to filter objectives based on the selected platform
 * using the following rules:
 * 1. if a platform is selected, return objectives that have at least one experiment group with that platform, OR
 * 2. objectives associated with experiment groups that have no platform set;
 * 3. always include "None" objective
 */
const filteredObjectives = computed(() => {
    if (!selectedPlatform.value) {
        return experimentObjectives.value;
    }
    return experimentObjectives.value
        .filter((objective) => {
            return experimentGroups.value!.some((group) => {
                return (
                    group.objective === objective &&
                    (group.platformId === selectedPlatform.value?.id || !group.platformId)
                );
            });
        })
        .concat(["None"]);
});

const filteredExperimentGroups = computed(() => {
    if (!objective.value && !selectedPlatform.value) {
        return experimentGroups.value;
    }

    let groups = experimentGroups.value;

    if (objective.value === "None") {
        groups = experimentGroups.value!.filter((group) => group.objective === null);
    } else if (objective.value) {
        groups = experimentGroups.value!.filter((group) => group.objective === objective.value);
    }

    if (selectedPlatform.value) {
        groups = groups!.filter(
            (group) => group.platformId === selectedPlatform.value!.id || !group.platformId
        );
    }
    return groups;
});

const isTreatmentsAndExperimentDesignConfigVisible = computed(() => {
    if (draft.value === true) {
        return false;
    }

    const selectedGroup = selectedExperimentGroup.value;
    if (_.isNil(selectedGroup)) {
        return true;
    }

    return selectedExperimentGroup.value.schemaConfig.hasThingSchemas;
})

watch(selectedExperimentGroupId, async () => {
    if (selectedExperimentGroup.value) {
        await withLoadingScreen(async () => {
            await setExperimentMetadata(selectedExperimentGroup.value.id);
            await setExperimentDesignConfig(selectedExperimentGroup.value.id);
            await fetchRelationshipConfigsForExperimentGroup(selectedExperimentGroup.value);
        });
    }
});

watch(objective, (newValue, oldValue) => {
    if (oldValue !== newValue) {
        selectedExperimentGroupId.value = undefined;
    }
});

watch(selectedPlatform, (newValue, oldValue) => {
    if (oldValue !== newValue) {
        selectedExperimentGroupId.value = undefined;

        // if the objective is no longer available for the selected platform, clear it
        if (!filteredObjectives.value.includes(objective.value)) {
            objective.value = "";
        }
    }
});

const selectedExperimentGroup = computed(() => {
    return experimentGroups.value.find((experimentGroup) => {
        return experimentGroup.id === selectedExperimentGroupId.value;
    })!;
});

function useConfluenceExperimentSetupCheckboxChanged(): void {
    if (useConfluenceExperimentSetup.value === false) {
        emit("exitConfluenceSetup");
    }
}

function onExperimentDesignConfigFormChanged(updatedExperimentDesignConfig: ExperimentDesignConfig): void {
    experimentDesignConfig.value = updatedExperimentDesignConfig;
}

function onRelationshipSetupFormChanged(experimentRelationshipForSetupDTOs: ExperimentRelationshipsForSetupDTO[]): void {
    experimentRelationships.value = experimentRelationshipForSetupDTOs;
}

async function validate() {
    if (isTreatmentsAndExperimentDesignConfigVisible.value === true) {
        const expDesignConfigForm = experimentDesignConfigForm.value;
        const expDesignConfigFormIsValid = await expDesignConfigForm.validate();

        if (!expDesignConfigFormIsValid) {
            return false;
        }

        if (_.isNil(treatmentsFile.value)) {
            EventBus.$emit("globalAlert", `Treatments file must be provided if not setting up a draft experiment`);
            return false;

        }
    }

    for (const experimentRelationshipConfig of experimentRelationshipConfigForTypes.value) {
        const relationshipsForType = experimentRelationships.value.filter((relationship) => {
            return relationship.type === experimentRelationshipConfig.type;
        })

        const requiredRelationshipConfigs = experimentRelationshipConfig.relationshipConfigs.filter((config) => {
            return config.required;
        })

        for (const relationshipConfig of requiredRelationshipConfigs) {
            const relationshipsForConfig = relationshipsForType.filter((relationship) => {
                return relationship.registrySchemaId === relationshipConfig.registrySchemaId
            });

            if (relationshipsForConfig.length === 0) {
                EventBus.$emit("globalAlert", `${experimentRelationshipConfig.type} ${relationshipConfig.registrySchema.displayName} is required`);
                return false;
            }

            for (const relationshipForConfig of relationshipsForConfig) {
                if (relationshipForConfig.registryEntryIds.length === 0) {
                    EventBus.$emit("globalAlert", `${experimentRelationshipConfig.type} ${relationshipConfig.registrySchema.displayName} is required`);
                    return false;
                }
            }
        }
    }

    const expSetupForm = setupForm.value;
    const expSetupFormValidationResult = await expSetupForm.validate();

    if (!expSetupFormValidationResult.valid) {
        const errors = expSetupFormValidationResult.errors;
        const msgs = errors.map((error) => {
            return error.errorMessages.join(", ");
        });
        EventBus.$emit("globalAlert", `There are validation errors: ${msgs.join(", ")}`);
        return false;
    } else {
        return true;
    }
}

async function setExperimentMetadata(experimentGroupId: string) {
    if (!experimentGroupId) {
        experimentMetadata.value = {};
    }
    try {
        const response = await experimentGroupsAPI.fetchExperimentGroup(experimentGroupId);
        experimentMetadata.value = response.experimentMetadata || {};
    } catch (error) {
        experimentMetadata.value = {};
    }
}

async function setExperimentDesignConfig(experimentGroupId: string) {
    if (!experimentGroupId) {
        experimentDesignConfig.value = null;
    }
    try {
        const experimentGroupLevelDesignConfigWithOptions = await experimentDesignConfigsAPI.fetchForExperimentGroupId(experimentGroupId);
        experimentDesignConfig.value = experimentGroupLevelDesignConfigWithOptions.experimentDesignConfig || null;
        experimentalUnitOptions.value = experimentGroupLevelDesignConfigWithOptions.experimentalUnitOptionsFromSchema;
    } catch (error) {
        experimentDesignConfig.value = null;
        experimentalUnitOptions.value = [];
    }
}

async function fetchRelationshipConfigsForExperimentGroup(experimentGroup: ExperimentGroupDTO) {
    experimentRelationshipConfigForTypes.value = await experimentRelationshipConfigAPIForSetup.fetchRelationshipConfigsForExperimentSetup(experimentGroup.id);
}

async function save() {
    await withLoadingScreen(async () => {
        if (await validate()) {
            const experiment = {
                displayName: name.value,
                folderId: selectedPlatform.value!.folderId,
                alternativeIdentifier: null,
                startDate: startDate.value.toISOString(),
                isUsingMeasurements: true,
                experimentGroupId: selectedExperimentGroupId.value!,
                status: null,
                qcStatus: null,
                metadata: experimentMetadata.value,
                relationships: experimentRelationships.value,
                containerTypeRegistryEntryId: draft.value ? null : experimentDesignConfig.value?.containerTypeRegistryEntryId ?? null,
                numberOfReplicates: draft.value ? null : experimentDesignConfig.value?.numberOfReplicates ?? null,
                rowColumnLayout: draft.value
                    ? null
                    : (experimentDesignConfig.value?.rowColumnLayout ?? null),
                randomizationType: draft.value ? null : experimentDesignConfig.value?.randomizationType ?? null,
                experimentalUnit: draft.value ? null : experimentDesignConfig.value?.experimentalUnit ?? null,
                conditionAssignmentStrategy: draft.value ? null : experimentDesignConfig.value?.conditionAssignmentStrategy ?? null,
                numberOfExperiments: numberOfExperiments.value
            };

            const formData = new FormData();
            if (!draft.value) {
                formData.append("treatments-setup-file", treatmentsFile.value!);
            }
            const experimentJson = JSON.stringify(experiment);
            const blob = new Blob([experimentJson], { type: "application/json" });
            formData.append("experiment", blob);

            let createdExperiments: ExperimentSetupResult[] = [];

            for (let i = 0; i < numberOfExperiments.value; i++) {
                try {
                    const savedExperiment = draft.value ?
                        await experimentsAPI.setupDraftExperiment(formData)
                        : await experimentsAPI.setupFinalizedExperiment(formData);
                    createdExperiments.push(savedExperiment);
                } catch (err) {
                    console.log(err);
                    const error = err as Error;
                    let errorMessage = error.message;
                    if (numberOfExperiments.value - i > 1) {
                        errorMessage += `\nThe additional ${numberOfExperiments.value - i - 1} requested experiment(s) will not be created.`;
                    }
                    EventBus.$emit("globalAlert", errorMessage);
                    throw error;
                }
            }

            if (createdExperiments.length > 0) {
                // push experiment list view
                router.push({ path: 'experiments/' });
            } else {
                // navigate to the experiment details page
                router.push({ path: `/experiment/${createdExperiments[0].experimentCode}` });
            }
        }
    });
}

function cancel() {
    router.push({ path: "/experiments" });
}
</script>
